import React, { useEffect, useRef, useState, useMemo } from "react";
import userService from "./user-service";
import { Toolbar } from "primereact/toolbar";
import { Button } from "primereact/button";
import { Navigate, useNavigate } from "react-router-dom";
import { Column } from "primereact/column";
import { DataTable, DataTableFilterMeta } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import ADPUsrService from "../../../../../services/admin/ADPUsrService";

import { FilterMatchMode, FilterOperator } from 'primereact/api';
import BreadcrumbComponent from "../../../../../components/Breadcrumb";
import { Avatar } from "primereact/avatar";
import { OverlayPanel } from "primereact/overlaypanel";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { useToast } from "../../../../../components/Toast";

interface DropdownItem {
    name: string;
    code: string;
    facilityCode: string;
    fullName: string;
    accessRoleId: string;
    fullDesc: string;
    departmentCode: string;
}

const defaultFilters: DataTableFilterMeta = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    fullName: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    icNo: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    userId: {
        operator: FilterOperator.OR,
        constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
    activity: { value: null, matchMode: FilterMatchMode.BETWEEN },
    verified: { value: null, matchMode: FilterMatchMode.EQUALS },
};
const UserList: React.FC = () => {
    const [users, setUsers] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [filters, setFilters] = useState<DataTableFilterMeta>(defaultFilters);
    const [globalFilterValue, setGlobalFilterValue] = useState<string>('');
    const [clientState, setClientState] = useState<any>(null);
    const navigate = useNavigate();
    const [dialog, setDialog] = useState(false);

    const { showToast } = useToast();

    const [facilityCodeDropdown, setFacilityCodeDropdown] = useState<DropdownItem[]>([]);
    const facilityCodeDropdownOptions = useMemo(() => facilityCodeDropdown, [facilityCodeDropdown]);
    const [accessRightDropdown, setAccessRightDropdown] = useState<DropdownItem[]>([]);
    const accessRightDropdownOptions = useMemo(() => accessRightDropdown, [accessRightDropdown]);
    const [departmentDropdown, setDepartmentDropdown] = useState<DropdownItem[]>([]);
    const departmentDropdownOptions = useMemo(() => departmentDropdown, [departmentDropdown]);
    const [authorizationLevelDropdown, setAuthorizationLevelDropdown] = useState<DropdownItem[]>([]);
    const authorizationLevelDropdownOptions = useMemo(() => authorizationLevelDropdown, [authorizationLevelDropdown]);

    const [shouldFetch, setShouldFetch] = useState(false);

    const [formData, setFormData] = useState<any>(null);

    useEffect(() => {
        const fetchClientState = async () => {
            try {
                const clientStateItem = await userService.getClientState();
                setClientState(clientStateItem);
            } catch (error) {
                console.error('Error fetching client state:', error);
            }
        };

        fetchClientState();
    }, []);

    useEffect(() => {
        if (clientState) {
            const fetchUserList = async () => {
                try {
                    const response = await userService.userList(clientState.facilityCode);
                    setUsers(response);
                } catch (error) {
                    console.error('Error fetching user data:', error);
                } finally {
                    setLoading(false);
                }
            };

            fetchUserList();
        }
    }, [clientState, shouldFetch]);

    function handlePrint(): void {
        window.print();
    };

    const handleCreate = () => {
        navigate(`/admin/user-edit`);
    };

    const handleFaciltyDropdownChange = (e: any) => {
        const { id, value } = e.target;
        setFormData({
            ...formData,
            [id]: value
        });

        userService.getAcsDropdown(value).then((data) => {
            setAccessRightDropdown(data);
        });
    };

    const handleDropdownChange = (e: any) => {
        const { id, value } = e.target;
        setFormData({
            ...formData,
            [id]: value
        });
    };

    const opRef = useRef<OverlayPanel>(null); // Reference for OverlayPanel
    const [selectedImage, setSelectedImage] = useState<string>(""); // State to store clicked image

    const handleImageClick = (event: React.MouseEvent<HTMLImageElement>, rowData: any) => {
        setSelectedImage(rowData.imageUrl || "https://www.primefaces.org/wp-content/uploads/2020/05/placeholder.png"); // Set image state
        if (opRef.current) {
            opRef.current.toggle(event); // Show overlay
        }
    };

    const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        let _filters = { ...filters };

        // @ts-ignore
        _filters['global'].value = value;

        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const initFilters = () => {
        setFilters(defaultFilters);
        setGlobalFilterValue('');
    }
    const renderHeader = () => {
        return (
            <div className="flex justify-content-between">
                {/* <Button type="button" icon="pi pi-filter-slash" label="Clear" outlined onClick={clearFilter} /> */}
                <div>
                    {/* <InputIcon className="pi pi-search" /> */}
                    <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Keyword Search" />
                </div>
                <div>
                    <Button icon="pi pi-print" severity="danger" style={{ marginRight: '.1em' }} onClick={() => handlePrint()} />
                    <Button label="New" icon="pi pi-plus" style={{ marginRight: '.5em' }} onClick={() => handleCreate()} />
                </div>
            </div>
        );
    };

    const header = renderHeader();

    const representativeBodyTemplate = (rowData: any) => {
        return (
            <React.Fragment>
                <img
                    alt={rowData.fullName || "User Avatar"}
                    src={rowData.imageUrl ? rowData.imageUrl : "https://www.primefaces.org/wp-content/uploads/2020/05/placeholder.png"}
                    onError={(e) => (e.currentTarget.src = "https://www.primefaces.org/wp-content/uploads/2020/05/placeholder.png")}
                    width={30}
                    height={30}
                    style={{ verticalAlign: "middle", borderRadius: "50%", cursor: "pointer" }}
                    onClick={(event) => handleImageClick(event, rowData)}
                />
                <OverlayPanel ref={opRef} appendTo={document.body} showCloseIcon>
                    <img
                        src={selectedImage}
                        alt="User Preview"
                        style={{ maxWidth: "300px", borderRadius: "10px" }}
                    />
                </OverlayPanel>

                <span style={{ marginLeft: ".5em", verticalAlign: "middle" }}>
                    {rowData.fullName}
                </span>
            </React.Fragment>
        );
    };



    const handleEdit = (userId: string) => {
        navigate('/admin/user-edit', {
            state: { userId: userId },
        });
    };

    const handleView = (userId: string) => {
        navigate(`/admin/user-view`, {
            state: { userId: userId },
        });
    };

    const handleDelete = (userId: string) => {
        ADPUsrService.delete(userId).then(() => {
            setUsers(users.filter((user: any) => user.user_id !== userId));
        });
    };

    const handleAddTenant = (rowData: any) => {

        userService.getUserTnt(rowData.userId).then((data) => {
            const codesToExclude = data.map((item: any) => item.facilityCode);
            userService.getFacilityDropdown(codesToExclude).then((data) => {
                setFacilityCodeDropdown(data);
            });
        });
        userService.getDeptDropdown().then((data) => {
            setDepartmentDropdown(data);
        });

        userService.getUserTypeDropdown('SYSTEM_ACTOR', 'ADP').then((data) => {
            setAuthorizationLevelDropdown(data);
        });

        setFormData({
            ...formData,
            userId: rowData.userId
        });

        setDialog(true);
    };

    const confirmationDialogFooter = () => {

        function addTenant(): void {
            const currentDate = new Date().toLocaleString("en-US", { timeZone: clientState.timeZone });

            formData.modifiedDate = new Date(currentDate);
            formData.modifiedBy = clientState.userId;
            formData.insertDate = new Date(currentDate);
            formData.insertBy = clientState.userId;


            userService.createUsrtnt(formData).then(() => {
                showToast(clientState.success);
                setFormData(null);
                setShouldFetch(true);
                setDialog(false);
            }).catch((error) => {
                clientState.error.detail = error.response?.data?.message || 'Failed to create record.';;
                showToast(clientState.error);
            });
        };

        return (
            <>
                <Button type="button" label="Cancel" icon="pi pi-times" style={{ marginRight: '.5em' }} className="p-button-warning" onClick={() => setDialog(false)} />
                <Button label="Save" icon="pi pi-check" style={{ marginRight: '.5em' }} className="p-button-success" onClick={addTenant} autoFocus />

            </>
        )
    };


    const actionTemplate = (rowData: any) => (
        <div style={{ display: 'flex', gap: '5px' }}>
            <Button
                label="View"
                icon="pi pi-eye"
                className="p-button-info p-button-sm "
                style={{ padding: '3px 5px', fontSize: '10px' }}
                onClick={() => handleView(rowData.userId)}
            />
            <Button
                label="Edit"
                icon="pi pi-pencil"
                className="p-button-warning p-button-sm "
                style={{ padding: '3px 5px', fontSize: '10px' }}
                onClick={() => handleEdit(rowData.userId)}
            />
            <Button
                label="Add Tanent"
                icon="pi pi-plus"
                className="p-button-purple p-button-sm "
                style={{ padding: '3px 5px', fontSize: '10px' }}
                onClick={() => handleAddTenant(rowData)}
            />
        </div>
    );

    return (
        <div className='grid'>
            <div className='col-12'>
                <div className='card-title'>
                    <h5>User</h5>
                    <BreadcrumbComponent />
                </div>
                <div className='card'>
                    <>
                        <DataTable value={users} showGridlines scrollable scrollHeight='auto' paginator rows={10} rowsPerPageOptions={[5, 10, 25, 50]}
                            filters={filters} globalFilterFields={['fullName', 'icNo', 'userId']} header={header} sortField="modifiedDate"
                            sortOrder={-1} // -1 => descending, 1 => ascending
                        >
                            <Column
                                header="No."
                                style={{ width: '5%' }}
                                body={(_, { rowIndex }) => rowIndex + 1} // Render row number
                            ></Column>
                            {/* <Column field="fullName" header="Name" style={{ width: '30%' }}></Column> */}
                            <Column
                                header="Name"
                                field="fullName"
                                body={representativeBodyTemplate}
                                style={{ width: '40%' }}
                            />
                            <Column field="icNo" header="IC No."></Column>
                            <Column field="userId" header="User ID"></Column>
                            <Column field="selectFacilityCodeHasOne.fullName" header="Facility"></Column>
                            <Column body={actionTemplate} header="Actions" style={{ width: '25%' }}></Column>

                        </DataTable>
                    </>


                </div>
            </div>
            <Dialog
                header="Add Tenant"
                visible={dialog}
                onHide={() => setDialog(false)}
                style={{ width: '500px' }}
                modal
                blockScroll
            >
                <div className="p-fluid">
                    <div className="field col-12 md:col-12">
                        <label>Facility</label>
                        <Dropdown
                            id="facilityCode"
                            value={formData?.facilityCode || ''}
                            onChange={handleFaciltyDropdownChange}
                            options={facilityCodeDropdownOptions}
                            optionLabel="fullName"
                            placeholder="Select One"
                            optionValue="facilityCode"
                        />
                    </div>
                    <div className="field col-12 md:col-12">
                        <label>Access Right</label>
                        <Dropdown
                            id="programRoleId"
                            value={formData?.programRoleId || ''}
                            onChange={handleDropdownChange}
                            options={accessRightDropdownOptions}
                            optionLabel="fullDesc"
                            placeholder="Select One"
                            optionValue="accessRoleId"
                        />
                    </div>
                    <div className="field col-12 md:col-12">
                        <label>Department Code</label>
                        <Dropdown
                            id="departmentCode"
                            value={formData?.departmentCode || ''}
                            onChange={handleDropdownChange}
                            options={departmentDropdownOptions}
                            optionLabel="fullDesc"
                            placeholder="Select One"
                            optionValue="departmentCode"
                        />
                    </div>
                    <div className="field col-12 md:col-12">
                        <label>System Actor</label>
                        <Dropdown
                            id="authorizationLevel"
                            value={formData?.authorizationLevel || ''}
                            onChange={handleDropdownChange}
                            options={authorizationLevelDropdownOptions}
                            optionLabel="fullDesc"
                            placeholder="Select One"
                            optionValue="referenceCode"
                        />
                    </div>
                </div>
                <div>
                    <br />
                </div>
                <Toolbar end={confirmationDialogFooter}></Toolbar>
            </Dialog>
        </div>

    );

};

export default UserList
